import React, { useState, useEffect } from 'react';
import { Search, Download, Eye, MessageCircle, Heart, Tag, Filter } from 'lucide-react';
import { useSupabaseData } from '../hooks/useSupabaseData';

interface Game {
  id: string;
  title: string;
  description?: string;
  content?: string;
  cover_image?: string;
  category: string;
  tags?: string[];
  author?: string;
  published_at?: string;
  created_at: string;
  updated_at: string;
  status?: string;
  view_count?: number;
  download_count?: number;
  download_link?: string;
  download_links?: Array<{
    type: string;
    url: string;
    password?: string;
    label: string;
  }>;
}

interface GameCardProps {
  game: Game;
}

const GameCard: React.FC<GameCardProps> = ({ game }) => {
  return (
    <div className="posts-item card ajax-item style3 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300">
      <div className="item-thumbnail relative group">
        <a href={`#/game/${game.id}`} className="block">
          <img 
            src={game.cover_image || 'https://images.igdb.com/igdb/image/upload/t_cover_big/co2nv6.jpg'} 
            alt={game.title}
            className="w-full h-48 object-cover rounded-t-lg group-hover:scale-105 transition-transform duration-300"
          />
        </a>
        <div className="absolute top-2 left-2">
          <span className="meta-pay but jb-yellow bg-yellow-400 text-white px-2 py-1 rounded-full text-xs font-medium">
            免费资源
          </span>
        </div>
      </div>
      
      <div className="item-body p-4">
        <h2 className="item-heading mb-3">
          <a 
            href={`#/game/${game.id}`}
            className="text-gray-900 hover:text-blue-600 font-medium line-clamp-2 transition-colors duration-200"
          >
            {game.title}
          </a>
        </h2>
        
        <div className="item-tags flex flex-wrap gap-1 mb-3 max-h-16 overflow-hidden">
          <span className="but c-blue bg-blue-100 text-blue-600 px-2 py-1 rounded text-xs">
            <Tag className="w-3 h-3 inline mr-1" />
            {game.category}
          </span>
          {game.tags?.slice(0, 3).map((tag, index) => (
            <span key={index} className="but bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
              #{tag}
            </span>
          ))}
        </div>
        
        <div className="item-meta flex justify-between items-center text-sm text-gray-500">
          <div className="meta-author flex items-center">
            <div className="avatar-mini w-6 h-6 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">
              A
            </div>
            <span>{new Date(game.published_at).toLocaleDateString('zh-CN')}</span>
          </div>
          
          <div className="meta-right flex items-center space-x-3">
            <div className="meta-comm flex items-center">
              <MessageCircle className="w-4 h-4 mr-1" />
              <span>{Math.floor(Math.random() * 50)}</span>
            </div>
            <div className="meta-view flex items-center">
              <Eye className="w-4 h-4 mr-1" />
              <span>{game.view_count || Math.floor(Math.random() * 1000)}</span>
            </div>
            <div className="meta-like flex items-center">
              <Heart className="w-4 h-4 mr-1" />
              <span>{game.download_count || Math.floor(Math.random() * 100)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ShenShi: React.FC = () => {
  // 隐藏页面的Header组件
  React.useEffect(() => {
    // 添加CSS样式来隐藏导航栏
    const style = document.createElement('style');
    style.id = 'shenshi-hide-nav';
    style.innerHTML = `
      .header, header, nav, .navbar, .navigation {
        display: none !important;
      }
      body {
        padding-top: 0 !important;
      }
    `;
    document.head.appendChild(style);
    
    // 组件卸载时移除样式
    return () => {
      const existingStyle = document.getElementById('shenshi-hide-nav');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);
  const { games, loading } = useSupabaseData();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredGames, setFilteredGames] = useState<Game[]>([]);

  useEffect(() => {
    if (games) {
      let filtered = games;
      
      if (searchTerm) {
        filtered = filtered.filter(game => 
          game.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          game.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      if (selectedCategory !== 'all') {
        filtered = filtered.filter(game => game.category === selectedCategory);
      }
      
      setFilteredGames(filtered);
    }
  }, [games, searchTerm, selectedCategory]);

  const categories = ['all', '3A游戏', '赛车游戏', '动作游戏', '冒险游戏', '解谜游戏'];

  const exportToCSV = () => {
    const csvData = filteredGames.map(game => {
      const baiduLink = game.download_links?.find(link => 
        link.type.includes('百度') || link.url.includes('pan.baidu.com')
      );
      
      return {
        标题: game.title,
        链接: `/game/${game.id}`,
        百度网盘链接: baiduLink?.url || '',
        百度网盘提取码: baiduLink?.password || ''
      };
    });

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).map(val => `"${val}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `ACG游戏资源_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="header-slider-container relative mb-8 bg-gradient-to-r from-pink-500 to-purple-600">
          <div className="header-slider-search py-20 text-center">
            <div className="max-w-4xl mx-auto px-4">
              <h1 className="text-4xl font-bold text-white mb-4">
                ACG游戏资源站 - 让人快乐的港湾
              </h1>
              <p className="text-pink-100 mb-8">
                为ACGN们提供了支持 galgame游戏资源、ACG资源、SLG游戏等免费下载，您可以敬请享受。
              </p>
              <div className="search-input max-w-2xl mx-auto">
                <div className="relative">
                  <input 
                    type="text"
                    placeholder="输入游戏名称搜索"
                    className="w-full px-6 py-3 rounded-full border-0 text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-4 focus:ring-pink-300"
                    disabled
                  />
                  <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
              <p className="text-gray-600">加载中...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="header-slider-container relative mb-8 bg-gradient-to-r from-pink-500 to-purple-600">
        <div className="header-slider-search py-20 text-center">
          <div className="max-w-4xl mx-auto px-4">
            <h1 className="text-4xl font-bold text-white mb-4">
              ACG游戏资源站 - 让人快乐的港湾
            </h1>
            <p className="text-pink-100 mb-8">
              为ACGN们提供了支持 galgame游戏资源、ACG资源、SLG游戏等免费下载，您可以敬请享受。
            </p>
            <div className="search-input max-w-2xl mx-auto">
              <div className="relative">
                <input 
                  type="text"
                  placeholder="输入游戏名称搜索"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-6 py-3 rounded-full border-0 text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-4 focus:ring-pink-300"
                />
                <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              </div>
            </div>
            
            <div className="search-keywords mt-6">
              <p className="text-pink-100 mb-2">热门搜索</p>
              <div className="flex justify-center flex-wrap gap-2">
                {['特工17', '美德', '深度觉醒', '后宫', '凤凰', '墮邪女神'].map((keyword) => (
                  <button 
                    key={keyword}
                    onClick={() => setSearchTerm(keyword)}
                    className="search_keywords bg-white bg-opacity-20 text-white px-3 py-1 rounded-full text-sm hover:bg-opacity-30 transition-all duration-200"
                  >
                    {keyword}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4">
        {/* Filter Section */}
        <div className="mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                      selectedCategory === category 
                        ? 'bg-pink-500 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {category === 'all' ? '全部游戏' : category}
                  </button>
                ))}
              </div>
              
              <button
                onClick={exportToCSV}
                className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
              >
                <Download className="w-4 h-4 mr-2" />
                导出CSV
              </button>
            </div>
          </div>
        </div>

        {/* Title Section */}
        <div className="home-tab-content mb-6">
          <div className="box-body">
            <div className="title-theme flex items-center">
              <h2 className="text-2xl font-bold text-gray-800 border-l-4 border-pink-500 pl-4">
                最近更新 NEWS
              </h2>
              <span className="ml-4 text-gray-500">共 {filteredGames.length} 个游戏</span>
            </div>
          </div>
        </div>

        {/* Games Grid */}
        <div className="posts-row grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
          {filteredGames.map((game) => (
            <GameCard key={game.id} game={game} />
          ))}
        </div>

        {/* No Results */}
        {filteredGames.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">🎮</div>
            <h3 className="text-xl text-gray-600 mb-2">没有找到相关游戏</h3>
            <p className="text-gray-500">尝试调整搜索条件或分类筛选</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShenShi;